import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveCanvas } from '../../../hooks';
import { useActiveCardFace } from '../../../hooks/use-active-card-face';
import { Drawer } from '../drawer/drawer';
import { DrawerPositions } from '../drawer/drawer-types';
import { HeaderContent, FooterContent } from './fragments';
import { ScaleDrawerProps } from './scale-drawer-types';
import styles from './scale-drawer.module.scss';
import { CUIText } from '@dotcom/crown-ui';

export const ScaleDrawer = ({ isOpen, onApply, onCancel, onChangeCallback }: ScaleDrawerProps): React.ReactElement => {
  const { isFront } = useActiveCardFace();

  const [originalScaleValue, setOriginalScaleValue] = useState<number | undefined>();
  const [currentScaleValue, setCurrentScaleValue] = useState<number | undefined>();

  const canvas = useActiveCanvas();

  const { t } = useTranslation();

  useEffect(() => {
    const activeObject = canvas?.current?.getActiveObject();

    if (activeObject?.type === 'image') {
      setOriginalScaleValue(activeObject.scaleX);
      setCurrentScaleValue(activeObject.data.scaleValue);
    }
  }, []);

  // if the user cancels the changes, revert the size of the image
  const handleClose = () => {
    const activeObject = canvas?.current?.getActiveObject();
    if (activeObject?.type === 'image') {
      activeObject.set({
        scaleX: originalScaleValue,
        scaleY: originalScaleValue,
        data: { ...activeObject.data, scaleValue: currentScaleValue },
      });
      canvas?.current?.renderAll();
    }
    onCancel();
  };

  return (
    <Drawer
      showVeil={false}
      hideCloseButton={true}
      addClass={styles['scale-drawer']}
      drawerPosition={DrawerPositions.Bottom}
      onClose={handleClose}
      isOpen={isOpen}
      headerContent={
        <HeaderContent
          onChangeCallback={onChangeCallback}
          minScale={isFront ? 100 : 10}
          scaleAtCenter={isFront ? undefined : 100}
          sliderCenter={isFront ? undefined : 100}
          mode={isFront ? 'left' : 'centered'}
        />
      }
      footerContent={<FooterContent onApply={onApply} onCancel={handleClose} />}
    >
      <CUIText as="span" variant="screen-reader">
        {t('scaleDrawer.scaleDrawer')}
      </CUIText>
    </Drawer>
  );
};
